import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import { TaskFilterType } from 'app/models/Tasks/Tasks';
import { getStatusList, getTaskGridFilters } from 'app/services/TaskServices/TaskServices';
import { taskSearchFilterName } from './SearchUtils';
import {
  getFilteredOptions,
  getFacilitySearchLookups,
  getOrderIdSearchLookups,
  getPlacementIdSearchLookups,
} from '../helper';
import { TaskSearchFilterName } from '../../Tasks/TaskManagement/TaskFilters/TaskFilterUtilities';
import { getTaskCategories } from 'app/services/SharedServices/SharedServices';

const sortOptionsByName = (list: any[]) =>
  [...list].sort((a, b) => ((a?.name ?? a?.Name ?? '') as string).localeCompare((b?.name ?? b?.Name ?? '') as string));

const formatStatusList = (list: any[], keyword?: string) => {
  if (!Array.isArray(list)) return [];

  const allowedNames = ['New', 'Pending', 'Completed'];

  return list
    .filter(data => allowedNames.includes(data?.name))
    .filter(data => !keyword || data?.name?.toLowerCase().includes(keyword.toLowerCase()))
    .map(data => ({ id: data.id, name: data.name, value: data.id }));
};

/** `modsLink`: placement link-task dialog — typeahead facility + sorted categories. Otherwise global search. */
export type TasksFilterVariant = 'globalSearch' | 'modsLink';

export class TasksFilter {
  constructor(
    public filterSpecs: { statusOpts?: any[] },
    public t: (key: string) => string,
    public styleClasses: any,
    public gridStateFilters: any,
    public variant: TasksFilterVariant = 'globalSearch',
  ) {}

  getTaskFilterSpecs = () => {
    const isModsLink = this.variant === 'modsLink';

    return [
    {
      filterName: this.t('search.globalSearch.task.filterNames.statusGlobalSearch'),
      name: taskSearchFilterName.status,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async (key: string) => {
        const list = await getStatusList();
        const opts = formatStatusList(Array.isArray(list) ? list : [], key);
        return getFilteredOptions(opts, key);
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.from'),
      name: taskSearchFilterName.from,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async (key: string) => {
        const data = await getTaskGridFilters({
          filterTypeId: TaskFilterType.CREATEDBY,
          searchText: key,
        });
        const list = Array.isArray(data) ? data : [];
        if (list.length === 0 && data === -1) return [];
        // Narrow by search text client-side (API may return full list; value can be number so avoid getFilteredOptions)
        const filtered =
          !key || !key.trim()
            ? list
            : list.filter((d: any) => (d.name || '').toLowerCase().includes(key.toLowerCase().trim()));
        const opts = filtered.map((d: any) => ({
          id: d.value,
          name: d.name ?? String(d.value),
          value: d.value,
        }));
        return opts;
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.recipients'),
      name: taskSearchFilterName.recipients,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async (key: string) => {
        const data = await getTaskGridFilters({
          filterTypeId: TaskFilterType.ASSIGNEDTO,
          searchText: key,
        });
        const list = Array.isArray(data) ? data : [];
        if (list.length === 0 && data === -1) return [];
        const filtered =
          !key || !key.trim()
            ? list
            : list.filter((d: any) => (d.name || '').toLowerCase().includes(key.toLowerCase().trim()));
        const opts = filtered.map((d: any) => ({
          id: d.value,
          name: d.name ?? String(d.value),
          value: d.value,
        }));
        return opts;
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.sentDate'),
      name: taskSearchFilterName.sentDateRange,
      type: FilterCompTypes.DATERANGEV2,
      variant: 'outlined',
      placeholder: this.t('search.globalSearch.task.filterNames.sentDate'),
      secondaryLabel: this.t('search.globalSearch.task.filterNames.sentDate'),
      useMaxWidth: true,
      maxWidth: 210,
      clickAway: true,
      disablePortal: true,
      quickSelect: false,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.followUp'),
      name: taskSearchFilterName.followUp,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async () => {
        return [
          { id: 1, name: 'Yes', value: true },
          { id: 2, name: 'No', value: false },
        ];
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.followUpDate'),
      name: taskSearchFilterName.followUpDateRange,
      type: FilterCompTypes.DATERANGEV2,
      variant: 'outlined',
      placeholder: this.t('search.globalSearch.task.filterNames.followUpDate'),
      secondaryLabel: this.t('search.globalSearch.task.filterNames.followUpDate'),
      useMaxWidth: true,
      maxWidth: 210,
      clickAway: true,
      disablePortal: true,
      quickSelect: false,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.priority'),
      name: taskSearchFilterName.priority,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async () => {
        return [
          { id: 1, name: 'Low', value: 1 },
          { id: 2, name: 'Normal', value: 2 },
          { id: 3, name: 'High', value: 3 },
        ];
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.optionApproval'),
      name: taskSearchFilterName.optionApproval,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async () => {
        return [
          { id: 1, name: 'Yes', value: true },
          { id: 2, name: 'No', value: false },
        ];
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.category'),
      name: taskSearchFilterName.categories,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async (key: string) => {
        const data = await getTaskCategories(true);
        let list = Array.isArray(data) ? data : [];
        if (isModsLink) {
          list = sortOptionsByName(list);
        }
        const filtered =
          !key || !key.trim()
            ? list
            : list.filter((d: any) =>
                ((d.name ?? d.Name) || '').toLowerCase().includes(key.toLowerCase().trim()),
              );
        const opts = filtered.map((d: any) => {
          const value = d.value ?? d.Value;
          const name = d.name ?? d.Name ?? String(value);
          return {
            id: value,
            name,
            value,
          };
        });
        return opts;
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.flagged'),
      name: 'flaggedFilter',
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async () => {
        return [
          { id: 1, name: 'Yes', value: true },
          { id: 2, name: 'No', value: false },
        ];
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.coverage'),
      name: 'coverageFilter',
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: async () => {
        return [
          { id: 1, name: 'Yes', value: true },
          { id: 2, name: 'No', value: false },
        ];
      },
      isSelectAll: true,
      applyOnClickAway: true,
      applyOnEnter: true,
    },
    isModsLink
      ? {
          filterName: this.t('search.globalSearch.order.filterNames.facility'),
          name: taskSearchFilterName.facility,
          type: FilterCompTypes.TYPEAHEADDROPDOWN,
          isMultiSelect: true,
          fetchOptions: getFacilitySearchLookups,
          isSelectAll: true,
          applyOnClickAway: true,
          applyOnEnter: true,
        }
      : {
          filterName: this.t('search.globalSearch.order.filterNames.facility'),
          name: taskSearchFilterName.facility,
          type: FilterCompTypes.FACILITYSEARCH,
          isMultiSelect: true,
          applyOnClickAway: true,
          applyOnEnter: true,
          isSelectAll: true,
        },
    {
      filterName: 'OID',
      name: taskSearchFilterName.oid,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: getOrderIdSearchLookups,
      applyOnClickAway: true,
      applyOnEnter: true,
      searchOnCharacterCount: 1,
      minWidth: 100,
    },
    {
      filterName: 'PID',
      name: taskSearchFilterName.pid,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: true,
      fetchOptions: getPlacementIdSearchLookups,
      applyOnClickAway: true,
      applyOnEnter: true,
      searchOnCharacterCount: 1,
      minWidth: 100,
    },

    {
      filterName: this.t('search.globalSearch.task.filterNames.unit'),
      name: TaskSearchFilterName.unit,
      type: FilterCompTypes.UNITSEARCHTASKS,
      applyOnClickAway: true,
      isMultiSelect: true,
      minWidth: 100,
    },
    {
      filterName: this.t('search.globalSearch.task.filterNames.candidate'),
      name: TaskSearchFilterName.candidateAggregate,
      type: FilterCompTypes.CANDIDATESEARCH,
      applyOnClickAway: true,
      isMultiSelect: true,
    },
  ];
  };
}
